
import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService, getCurrentUser, checkAuthenticationStatus } from './Authservice';

// Create Authentication Context
const AuthContext = createContext(null);

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔧 Initializing AuthContext...');
        
        // Clear session storage flags on page refresh to allow auth API call
        const isPageRefresh = performance.getEntriesByType('navigation')[0]?.type === 'reload';
        if (isPageRefresh) {
          sessionStorage.removeItem('authCallBeforeSaml');
          sessionStorage.removeItem('authCallAfterRedirect');
        }
        
        await authService.initialize();
        
        console.log('🔍 Checking authentication status on mount...');
        // Check authentication status via API call (cookie-based JWT)
        const authResult = await checkAuthenticationStatus();
        console.log('✅ Auth result on mount:', authResult);
        
        setIsAuthenticated(authResult.isAuthenticated);
        if (authResult.isAuthenticated && authResult.user) {
          setUser(authResult.user);
          console.log('👤 User set:', authResult.user);
        }
      } catch (error) {
        console.error('❌ Error in initializeAuth:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initializeAuth();
  }, []);

  // Listen for authentication changes (e.g., after SAML login)
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        console.log('🔄 Checking auth status (periodic check)...');
        // Use API-based authentication check instead of just sessionStorage
        const authResult = await checkAuthenticationStatus();
        console.log('📊 Periodic auth check result:', authResult);
        
        if (authResult.isAuthenticated !== isAuthenticated) {
          console.log(`🔄 Auth status changed: ${isAuthenticated} → ${authResult.isAuthenticated}`);
          setIsAuthenticated(authResult.isAuthenticated);
          if (authResult.isAuthenticated && authResult.user) {
            setUser(authResult.user);
            console.log('👤 User updated:', authResult.user);
          } else {
            setUser(null);
            console.log('🚪 User cleared');
          }
        }
      } catch (error) {
        console.error('❌ Error in periodic auth check:', error);
        // On error, consider user not authenticated
        if (isAuthenticated) {
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    };
    window.addEventListener('storage', checkAuthStatus);
    const interval = setInterval(checkAuthStatus, 5000);
    return () => {
      window.removeEventListener('storage', checkAuthStatus);
      clearInterval(interval);
    };
  }, [user, isAuthenticated]);

  // Login function
  const login = async () => {
    try {
      await authService.login();
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      throw error;
    }
  };

  // Refresh authentication state manually
  const refreshAuth = async () => {
    try {
      console.log('🔄 Manual auth refresh triggered...');
      const authResult = await checkAuthenticationStatus();
      console.log('✅ Manual auth refresh result:', authResult);
      
      setIsAuthenticated(authResult.isAuthenticated);
      if (authResult.isAuthenticated && authResult.user) {
        setUser(authResult.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Error in manual auth refresh:', error);
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  // Authentication context value
  const value = {
    user,
    login,
    logout,
    refreshAuth,
    isAuthenticated,
    loading
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <img src="/ajax-loading.gif" alt="Loading..." style={{ width: '64px', height: '64px' }} />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
